import { useMemo, useRef } from "react";
import useCatalogAccess from "../components/useCatalogAccess";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import ItemsTab from "../components/tabs/ItemsTab";
import CategoriesTab from "../components/tabs/CategoriesTab";
import SubcategoriesTab from "../components/tabs/SubcategoriesTab";
import SubfamiliesTab from "../components/tabs/SubfamiliesTab";
import FamiliesTab from "../components/tabs/FamiliesTab";
import EquivalentProducts from "./EquivalentProducts";
import {
  ITEMS_FAMILIES_UI_STYLES,
  readItemsFamiliesUser,
} from "../components/itemsFamiliesUi";
import "../PurchasingUi.css";
import "./CatalogWorkspace.css";

const TAB_CONFIG = {
  items: { en: "Items", es: "Artículos", Component: ItemsTab },
  families: { en: "Families", es: "Familias", Component: FamiliesTab },
  subfamilies: { en: "Subfamilies", es: "Subfamilias", Component: SubfamiliesTab },
  categories: { en: "Categories", es: "Categorías", Component: CategoriesTab },
  subcategories: { en: "Subcategories", es: "Subcategorías", Component: SubcategoriesTab },
};

export default function ItemsFamilies({ forcedTab = null }) {
  const { themeId, languageId } = usePmsTheme();
  const locale = languageId === "es" ? "es" : "en";
  const navigate = useNavigate();
  const tabRefs = useRef({});
  const currentUser = readItemsFamiliesUser();
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || currentUser?.active_business_unit_id;
  const access = useCatalogAccess(businessUnitId);

  const tabPermissions = useMemo(() => ({
    items: access.can("purchasing.items.view"),
    "equivalent-products": access.can("purchasing.equivalents.view"),
    subcategories: access.can("purchasing.categories.view"),
    categories: access.can("purchasing.categories.view"),
    subfamilies: access.can("purchasing.items.view"),
    families: access.can("purchasing.items.view"),
  }), [access]);

  const availableTabs = useMemo(
    () =>
      Object.keys(TAB_CONFIG).filter((tabKey) => Boolean(tabPermissions[tabKey])),
    [tabPermissions]
  );

  // Catalog deep links select a tab; equivalent products remains a sidebar workspace.
  const activeTab = forcedTab || availableTabs[0] || null;
  const isEquivalentWorkspace = activeTab === "equivalent-products";

  const selectTab = (tab) => {
    if (!tabPermissions[tab] || tab === activeTab) return;
    const nextSearch = new URLSearchParams(searchParams);
    if (businessUnitId) nextSearch.set("business_unit_id", businessUnitId);
    navigate({ pathname: `/purchasing/items/${tab}`, search: nextSearch.toString() });
  };
  const moveTabFocus = (event, tab) => {
    const index = availableTabs.indexOf(tab);
    const nextIndex = event.key === "ArrowRight" ? (index + 1) % availableTabs.length
      : event.key === "ArrowLeft" ? (index - 1 + availableTabs.length) % availableTabs.length
      : event.key === "Home" ? 0 : event.key === "End" ? availableTabs.length - 1 : null;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = availableTabs[nextIndex];
    selectTab(nextTab);
    tabRefs.current[nextTab]?.focus();
  };

  if ((forcedTab && !tabPermissions[forcedTab]) || !activeTab) {
    return (
      <div className="pilcore-purchasing-ui pu-catalog spx-shell" data-theme={themeId}>
        <div className="spx-empty" role="alert">{locale === "es" ? "No tienes permiso para acceder a esta sección del catálogo." : "You do not have permission to access this catalog section."}</div>
      </div>
    );
  }
  const ActiveComponent = isEquivalentWorkspace ? EquivalentProducts : TAB_CONFIG[activeTab].Component;
  return (
    <div className="pilcore-purchasing-ui pu-catalog spx-shell" data-theme={themeId}>
      <style>{ITEMS_FAMILIES_UI_STYLES}</style>

      <div className="ifm-page">
        {!isEquivalentWorkspace && (
          <nav className="catalog-tabBar" aria-label={locale === "es" ? "Catálogo de artículos" : "Item catalog"}>
            <div className="catalog-tabs" role="tablist" aria-label={locale === "es" ? "Secciones del catálogo" : "Catalog sections"}>
              {availableTabs.map((tabKey) => (
                <button
                  key={tabKey}
                  type="button"
                  ref={(node) => { tabRefs.current[tabKey] = node; }}
                  id={`catalog-tab-${tabKey}`}
                  role="tab"
                  aria-selected={activeTab === tabKey}
                  aria-controls="catalog-tab-panel"
                  tabIndex={activeTab === tabKey ? 0 : -1}
                  className="catalog-tab"
                  onKeyDown={(event) => moveTabFocus(event, tabKey)}
                  onClick={() => selectTab(tabKey)}
                >
                  {TAB_CONFIG[tabKey][locale]}
                </button>
              ))}
            </div>
          </nav>
        )}
        <div
          id="catalog-tab-panel"
          className="catalog-panel"
          role={isEquivalentWorkspace ? undefined : "tabpanel"}
          aria-labelledby={isEquivalentWorkspace ? undefined : `catalog-tab-${activeTab}`}
          tabIndex={isEquivalentWorkspace ? undefined : 0}
        >
          <ActiveComponent key={`${activeTab}:${businessUnitId || "none"}`} businessUnitId={businessUnitId} />
        </div>
      </div>
    </div>
  );
}
